import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  Box,
  Modal,
  TextField,
  InputAdornment,
  CircularProgress,
  Button,
} from "@mui/material";
import { Check, Pencil, Trash, X } from "lucide-react";

const options = ["ATOZ", "SGST", "CGST", "ATOZ"];

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 573,
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "0.5rem",
  boxShadow: 24,
};

const columns = [
  { id: "billName", label: "Bill Name", minWidth: 140 },
  { id: "billDate", label: "Bill Date", minWidth: 120 },
  { id: "total", label: "Total", minWidth: 120 },
  { id: "pending", label: "Pending", minWidth: 120 },
  { id: "amountAdjusted", label: "Amount Adjusted", minWidth: 160 },
];

const AddReceiptTable = ({ onBillsChange }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [billName, setBillName] = useState("");
  const [amount, setAmount] = useState("");
  const [billNameModal, setBillNameModal] = useState("");
  const [amountModal, setAmountModal] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (onBillsChange) {
      onBillsChange(rows);
    }
  }, [rows, onBillsChange]);

  const handleClose = () => {
    setOpen(false);
    setEditingRow(null);
  };

  const handleOpen = row => {
    setEditingRow(row);
    setBillNameModal(row.billName);
    setAmountModal(row.amountAdjusted);
    setOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editingRow) return;

    const updatedRows = rows.map(row =>
      row.id === editingRow.id
        ? { ...row, billName: billNameModal, amountAdjusted: amountModal }
        : row,
    );

    setRows(updatedRows);
    handleClose();
  };

  const handleBillNameChange = (event, value) => {
    setBillName(value);
  };

  const handleAddRow = () => {
    if (!billName || !amount) return;

    setIsLoading(true);

    // Simulate API delay
    setTimeout(() => {
      const newRow = {
        id: `row-${Date.now()}`,
        billName,
        billDate: "12/03/2024", // In a real app, you'd probably want a date picker here
        total: "40,000",
        pending: "23,990",
        amountAdjusted: amount,
      };

      setRows([...rows, newRow]);
      setBillName("");
      setAmount("");
      setIsLoading(false);
    }, 300);
  };

  const handleDeleteRow = id => {
    setRows(rows.filter(row => row.id !== id));
  };

  return (
    <div className="w-full">
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: "none",
          border: "1px solid #E5E7EB",
          borderRadius: "0.5rem",
        }}>
        <TableContainer
          sx={{ maxHeight: 440 }}
          className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={column.id}
                    align={column.id === "amountAdjusted" ? "center" : "center"}
                    sx={{
                      minWidth: column.minWidth,
                      backgroundColor: "#F9FAFB",
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#374151",
                      borderBottom: "2px solid #E5E7EB",
                    }}
                    className="font-outfit">
                    {column.label}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                    width: 100,
                  }}
                  className="font-outfit">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <div className="flex items-center justify-center py-4">
                      <CircularProgress
                        size={40}
                        style={{ color: "#5652B0" }}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <div className="py-8 text-gray-500">
                      No bills added yet. Use the form below to add bills for
                      adjustment.
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(row => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                      }}
                      className="font-outfit">
                      {row.billName}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                      }}
                      className="font-outfit">
                      {row.billDate}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                      }}
                      className="font-outfit">
                      ₹{row.total}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                      }}
                      className="font-outfit">
                      ₹{row.pending}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                      }}
                      className="font-outfit">
                      ₹{row.amountAdjusted}
                    </TableCell>
                    <TableCell align="center">
                      <div className="flex items-center justify-center gap-3">
                        <button
                          onClick={() => handleOpen(row)}
                          className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors">
                          <Pencil size={16} className="text-[#5652B0]" />
                        </button>
                        <button
                          onClick={() => handleDeleteRow(row.id)}
                          className="p-1.5 rounded-md bg-red-50 hover:bg-red-100 transition-colors">
                          <Trash size={16} className="text-red-500" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Input form for adding new bills */}
      <div className="flex items-end gap-4 mt-4">
        <div className="w-2/5">
          <Autocomplete
            id="bill-name-select"
            freeSolo
            value={billName}
            size="small"
            options={options}
            onChange={handleBillNameChange}
            renderInput={params => (
              <TextField
                {...params}
                label="Bill Name"
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#5652B0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                }}
              />
            )}
          />
        </div>
        <div className="w-2/5">
          <TextField
            label="Amount"
            size="small"
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            required
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <span className="text-gray-400">₹</span>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#E5E7EB",
                },
                "&:hover fieldset": {
                  borderColor: "#5652B0",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#5652B0",
                },
              },
            }}
          />
        </div>
        <div>
          <Button
            onClick={handleAddRow}
            disabled={!billName || !amount || isLoading}
            variant="contained"
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": {
                backgroundColor: "#4A47A3",
              },
              textTransform: "none",
              fontFamily: "Outfit, sans-serif",
              boxShadow: "none",
              padding: "8px 16px",
            }}>
            {isLoading ? <CircularProgress size={24} color="inherit" /> : "Add"}
          </Button>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200">
              <h2 className="text-xl font-semibold font-outfit text-gray-800">
                Edit Bill
              </h2>
              <button
                onClick={handleClose}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <TextField
                label="Bill Name"
                fullWidth
                size="small"
                value={billNameModal}
                onChange={e => setBillNameModal(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#5652B0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                }}
              />

              <TextField
                label="Amount Adjusted"
                fullWidth
                size="small"
                type="number"
                value={amountModal}
                onChange={e => setAmountModal(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span className="text-gray-400">₹</span>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#5652B0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                }}
              />
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{
                  borderColor: "#E5E7EB",
                  color: "#374151",
                  "&:hover": {
                    borderColor: "#D1D5DB",
                    backgroundColor: "#F9FAFB",
                  },
                  textTransform: "none",
                  fontFamily: "Outfit, sans-serif",
                }}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                variant="contained"
                sx={{
                  backgroundColor: "#5652B0",
                  "&:hover": {
                    backgroundColor: "#4A47A3",
                  },
                  textTransform: "none",
                  fontFamily: "Outfit, sans-serif",
                  boxShadow: "none",
                }}>
                Save Changes
              </Button>
            </div>
          </div>
        </Box>
      </Modal>

      {isLoading && (
        <div className="absolute top-0 left-0 right-0">
          <div className="h-1 w-full overflow-hidden">
            <div className="animate-progress bg-[#5652B0] h-1"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddReceiptTable;
